import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ClockIcon } from '@heroicons/react/24/outline';
import API_URL from '../../config/api';

const PaymentPending = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [checks, setChecks] = useState(0);

    const orderTrackingId = searchParams.get('order_tracking_id') || searchParams.get('OrderTrackingId') || searchParams.get('transaction_id') || searchParams.get('session_id') || '';
    const txRef = searchParams.get('tx_ref') || searchParams.get('reference') || '';

    // Poll the backend for the final status of a pending payment.
    // Uses exponential backoff (3s, 6s, 12s, ... capped at 30s) instead of a
    // fixed 4s interval, and stops after ~5 minutes total. A 404 means the
    // transaction row hasn't been created yet (webhook latency) —
    // keep waiting instead of treating it as a hard failure.
    useEffect(() => {
        let cancelled = false;
        let attempts = 0;
        const MAX_ATTEMPTS = 12;
        const MAX_DELAY = 30000;

        const checkStatus = async () => {
            if (!txRef || attempts >= MAX_ATTEMPTS) return;
            attempts++;
            try {
                const token = localStorage.getItem('token') || localStorage.getItem('portalToken');
                const response = await fetch(`${API_URL}/api/payments/status/${txRef}`, {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : {}
                });
                if (response.ok) {
                    const data = await response.json();
                    if (cancelled) return;
                    if (data.status === 'successful') {
                        navigate('/payment/success', { replace: true, state: { order_tracking_id: orderTrackingId, tx_ref: txRef } });
                        return;
                    }
                    if (data.status === 'failed' || data.status === 'cancelled') {
                        navigate('/payment/failed', { replace: true, state: { order_tracking_id: orderTrackingId, tx_ref: txRef } });
                        return;
                    }
                }
                // 404: transaction not yet recorded — keep polling; the webhook
                // may still be in flight. Other non-ok statuses are also retried.
            } catch (e) {
                // Transient errors — keep polling
            }
            if (!cancelled) {
                setChecks(attempts);
                const delay = Math.min(3000 * Math.pow(2, attempts - 1), MAX_DELAY);
                setTimeout(checkStatus, delay);
            }
        };

        const t = setTimeout(checkStatus, 3000);
        return () => { cancelled = true; clearTimeout(t); };
    }, [orderTrackingId, txRef, navigate]);

    return (
        <div className="min-h-screen relative bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center px-4 overflow-hidden">
            <div className="relative max-w-md w-full bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/40 dark:border-gray-700/50 p-8 text-center animate-imageFadeIn">
                <div className="flex justify-center mb-6">
                    <div className="relative">
                        <div className="absolute inset-0 rounded-full bg-amber-400/40 animate-ping" aria-hidden="true"></div>
                        <div className="relative rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 p-4 shadow-lg shadow-amber-500/30">
                            <ClockIcon className="h-14 w-14 text-white" />
                        </div>
                    </div>
                </div>

                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Payment Pending</h1>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Your payment is still being processed. This page will update automatically once we receive confirmation.
                </p>

                <div className="flex items-center justify-center gap-2 text-sm text-amber-600 dark:text-amber-400 mb-6">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Checking status{checks > 0 ? ` (attempt ${checks})` : ''}...
                </div>

                <button
                    onClick={() => navigate('/pricing')}
                    className="w-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 font-bold py-3 px-4 rounded-xl hover:opacity-90 transition-all"
                >
                    Back to Pricing
                </button>

                <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
                    Need help? Contact us at{' '}
                    <a href="mailto:ospitoplus@gmail.com" className="text-amber-600 dark:text-amber-400 hover:underline">
                        ospitoplus@gmail.com
                    </a>
                </p>
            </div>
        </div>
    );
};

export default PaymentPending;
